// https://photoswipe.com/getting-started
import { ref, unref, onMounted, onBeforeUnmount } from 'vue'
import { _lazyload } from '@/utils/lazyload'
import PhotoSwipeLightbox from 'photoswipe/lightbox'
import 'photoswipe/style.css'

// 计算文字截断
const calcEllipsised = (root, content, rows = 3) => {
  const pxToNum = (value) => {
    if (!value) return 0
    const match = value.match(/^\d*(\.\d*)?/)
    return match ? Number(match[0]) : 0
  }

  const cloneContainer = () => {
    if (!root) return

    const originStyle = window.getComputedStyle(root)
    const container = document.createElement('div')
    const styleNames = Array.prototype.slice.apply(originStyle)
    styleNames.forEach((name) => {
      container.style.setProperty(name, originStyle.getPropertyValue(name))
    })

    container.style.position = 'fixed'
    container.style.zIndex = '-9999'
    container.style.top = '-9999px'
    container.style.height = 'auto'
    container.style.minHeight = 'auto'
    container.style.maxHeight = 'auto'
    container.style.display = 'block'
    container.style.bottom = 'auto'
    container.style.left = 'auto'
    container.style.right = 'auto'

    container.innerText = content
    document.body.appendChild(container)
    return container
  }

  let hasAction = false
  const container = cloneContainer()
  if (!container) return hasAction

  const { paddingBottom, paddingTop, lineHeight } = container.style
  const maxHeight =
    (Number(rows) + 0.5) * pxToNum(lineHeight) + pxToNum(paddingTop) + pxToNum(paddingBottom)
  if (maxHeight < container.offsetHeight) {
    hasAction = true
  } else {
    hasAction = false
  }

  document.body.removeChild(container)
  return hasAction
}

/**
 * @desc 函数防抖
 * @param {Function} callback 回调函数
 * @param {Number} wait 延迟毫秒数
 * @param {Boolean} immediate 立即执行
 *
 * @return {Function}
 */
const debounce = (callback, wait = 300, immediate = false) => {
  let timer
  return function () {
    let ctx = this
    let args = arguments

    if (timer) {
      clearTimeout(timer)
    }

    if (immediate) {
      let callNow = !timer
      timer = setTimeout(() => {
        timer = null
      }, wait)
      if (callNow) {
        callback.apply(ctx, args)
      }
    } else {
      timer = setTimeout(() => {
        callback.apply(ctx, args)
      }, wait)
    }
  }
}

const useSimpleGallery = (images = [], changeFn, other = {}) => {
  // 模式
  const mode = other?.mode || 'default'
  // 是否初始化
  let isInit = true
  if ('isInit' in other) isInit = other?.isInit

  const mobileSize = 767
  const lightbox = ref(null)
  const closeSVGString =
    '<svg aria-hidden=true class=pswp__icn height=24px viewBox="0 0 24 24"width=24px><g fill=none fill-rule=evenodd stroke=none stroke-width=1><g transform="translate(-312.000000, -806.000000)"><g transform="translate(312.000000, 806.000000)"><rect height=24 id=bg width=24 x=0 y=0></rect><path d="M19.6774438,4.3225585 L19.5880243,4.24227785 C19.1555846,3.89439503 18.5214081,3.92115525 18.1200048,4.3225585 L12,10.4414124 L5.87997898,4.32253997 C5.4499226,3.89248359 4.75263341,3.89248359 4.3225585,4.3225585 L4.24227785,4.41197799 C3.89439503,4.84441768 3.92115525,5.47859426 4.3225585,5.87999751 L10.4427712,11.9986423 L4.32253997,18.1200233 C3.89248359,18.5500797 3.89248359,19.2473689 4.3225585,19.6774438 L4.41197799,19.7577245 C4.84441768,20.1056073 5.47859426,20.0788471 5.87999751,19.6774438 L12,13.5558723 L18.1200014,19.6774404 C18.5500797,20.1075187 19.2473689,20.1075187 19.6774438,19.6774438 L19.7577245,19.5880243 C20.1056073,19.1555846 20.0788471,18.5214081 19.6774438,18.1200048 L13.5572311,11.9986423 L19.6774404,5.88000088 C20.1075187,5.4499226 20.1075187,4.75263341 19.6774438,4.3225585 Z"fill=#FFFFFF fill-rule=nonzero id=路径></path></g></g></g></svg>'
  const arrowNextSVGString =
    '<svg aria-hidden=true class=pswp__icn height=16px version=1.1 viewBox="0 0 16 16"width=16px><title>icon_arrow_poll</title><g fill=none fill-rule=evenodd stroke=none stroke-width=1><g transform="translate(-1009.000000, -6130.000000)"><g transform="translate(352.000000, 1466.000000)"><g transform="translate(531.000000, 4652.000000)"><g id=">"transform="translate(134.000000, 20.000000) scale(-1, 1) translate(-134.000000, -20.000000) translate(118.000000, 4.000000)"><g id=icon/poll/arrow transform="translate(8.000000, 8.000000)"><g id=icon_arrow_poll transform="translate(8.000000, 8.000000) scale(-1, 1) rotate(-360.000000) translate(-8.000000, -8.000000) translate(0.000000, 0.000000)"><rect height=16 id=bg width=16 x=0 y=0></rect><path d="M4.42233027,1.07114461 L4.34364752,1.15621458 L4.26496305,1.26010008 C3.87370024,1.82601434 3.93585197,2.57230786 4.41359602,3.05005192 L9.79301191,8.42838138 L4.4135865,12.9510775 C3.86966863,13.4949954 3.86966863,14.3769037 4.41361318,14.9208483 C4.95755773,15.4647928 5.83946609,15.4647928 6.38341064,14.9208483 L12.4822066,9.67919515 C13.1725978,8.98880399 13.1725978,7.86945876 12.4822066,7.1790676 L6.38341064,1.08027162 C5.84192935,0.538790327 4.96024244,0.533964861 4.42233027,1.07114461 Z"fill=#FFFFFF fill-rule=nonzero id=路径></path></g></g></g></g></g></g></g></svg>'

  const initPS = () => {
    const _images = unref(images).map((v) => {
      return { ...v, width: 1, height: 1 }
    })
    const imgs = _images.map(() => {
      return new Image()
    })
    // 当幻灯片少于 3 张时，loop 选项始终为 false 。
    lightbox.value = new PhotoSwipeLightbox({
      mainClass: `disable-content-copy tvb-pswp${other?.mainClass ? ' ' + other.mainClass : ''}`,
      dataSource: _images,
      pswpModule: () => import('photoswipe'),
      zoom: false,
      bgOpacity: 0.9,
      preload: [1, 1], // 预加载当前图片的上一张和下一张
      secondaryZoomLevel: 1.2,
      maxZoomLevel: 1,
      preloaderDelay: 1000,
      allowPanToNext: false,
      clickToCloseNonZoomable: false,
      closeOnVerticalDrag: false,
      closeSVG: closeSVGString,
      arrowPrevSVG: arrowNextSVGString,
      arrowNextSVG: arrowNextSVGString,
      errorMsg: '圖像無法加載',
      paddingFn: (viewportSize) => {
        return {
          top: viewportSize.x <= mobileSize ? 0 : 56,
          bottom: viewportSize.x <= mobileSize ? 0 : 104 + 58,
          left: viewportSize.x <= mobileSize ? 0 : viewportSize.x * 0.08333333333333333,
          right: viewportSize.x <= mobileSize ? 0 : viewportSize.x * 0.08333333333333333
        }
      }
    })
    // console.log('🚀 ~ onMounted ~ ightbox.value:', lightbox.value)
    lightbox.value.on('initialLayout', () => {
      // 缩略图懒加载
      try {
        let bulletImages = document.querySelectorAll('.pswp__bullet_image')
        _lazyload(bulletImages)
      } catch (error) {
        console.error(error)
      }
    })

    let refresh = false
    lightbox.value.on('change', () => {
      if (refresh) return
      const pswp = lightbox.value.pswp
      const slide = pswp.currSlide
      const index = pswp.currIndex
      if (slide) {
        const data = slide.data
        const img = imgs[index]
        if (!img.src) {
          img.onload = () => {
            data.width = img.naturalWidth
            data.height = img.naturalHeight
            refresh = true
            pswp.refreshSlideContent(index)
            refresh = false
          }
          img.src = data.src
        }
        changeFn && changeFn(pswp)
      }
    })

    // 错误图片处理
    // lightbox.value.addFilter('contentErrorElement', () => {
    //   const el = document.createElement('div')
    //   el.className = 'pswp__error-msg'
    //   el.innerHTML = `圖像無法加載`
    //   return el
    // })

    lightbox.value.on('uiRegister', function () {
      // 注入描述
      lightbox.value.pswp.ui.registerElement({
        name: 'custom-caption',
        isButton: false,
        appendTo: 'root',
        onInit: (el, pswp) => {
          lightbox.value.pswp.on('change', () => {
            if (mode === 'vote') {
              const title = pswp.currSlide.data.title || ''
              const subtitle = pswp.currSlide.data.subtitle || ''
              const desc = pswp.currSlide.data.desc || ''
              const desc2 = pswp.currSlide.data.desc2 || ''
              const ismobile = pswp.viewportSize.x <= mobileSize
              const hasAction1 = ismobile ? calcEllipsised(el, subtitle, 1) : false
              const hasAction2 = ismobile ? calcEllipsised(el, desc, 1) : false
              const hasAction3 = ismobile ? calcEllipsised(el, desc2, 1) : false
              const hasAction = hasAction1 || hasAction2 || hasAction3
              el.style.display = title || subtitle || desc || desc2 ? null : 'none'
              el.innerHTML = `<span class="viewer-vote-title">${title}</span><div class="viewer-vote-title_inner"><span>${subtitle}</span><span>${desc}</span><span>${desc2}</span>${
                hasAction
                  ? `<span class="viewer-ellipsis-action" onclick="this.parentNode.classList.toggle('viewer-all-action')"><i></i></span>`
                  : ''
              }</div>`
            } else {
              const desc = pswp.currSlide.data['data-desc'] || ''
              const hasAction = pswp.viewportSize.x <= mobileSize ? calcEllipsised(el, desc) : false
              el.style.display = desc ? null : 'none'
              el.innerHTML = `<span class="viewer-title_inner">${desc}${
                hasAction
                  ? `<span class="viewer-ellipsis-action" onclick="this.parentNode.classList.toggle('viewer-all-action')"><i></i></span>`
                  : ''
              }</span>`
            }
          })
        }
      })
      // 缩略图 thumbnail
      lightbox.value.pswp.ui.registerElement({
        name: 'thumbnail',
        className: 'pswp__bullets-indicator',
        isButton: false,
        appendTo: 'root',
        onInit: (el, pswp) => {
          const bullets = []
          let bullet
          let prevIndex = -1
          const dataSource = pswp.options.dataSource
          const wrapper = document.createElement('div')
          wrapper.className = 'pswp__bullets-indicator-wrapper'

          for (let i = 0; i < dataSource.length; i++) {
            const slideData = dataSource[i]

            bullet = document.createElement('div')
            bullet.className = 'pswp__bullet'
            bullet.onclick = (e) => {
              const target = e.target
              const thumbnailEl = target.tagName === 'IMG' ? target.parentElement : e.target
              if (thumbnailEl) {
                pswp.goTo(bullets.indexOf(thumbnailEl))
              }
            }

            const thumbnail = document.createElement('img')
            thumbnail.setAttribute('data-src', slideData.thumbnail || slideData.src || '')
            thumbnail.className = 'pswp__bullet_image'
            thumbnail.onerror = (e) => {
              e.target.style.display = 'none'
            }

            bullet.appendChild(thumbnail)
            wrapper.appendChild(bullet)
            bullets.push(bullet)
          }
          el.appendChild(wrapper)

          const setTransform = () => {
            const screenWidth = pswp.viewportSize.x
            const bulletWidth = bullet.offsetWidth
            const gutter = 10 // 间距
            const currIndex = pswp.currIndex
            // translateX: ((屏幕宽度 - 子元素宽度) / 2) - (子元素宽度 + 间距) * 当前index
            let translateX = (screenWidth - bulletWidth) / 2 - (bulletWidth + gutter) * currIndex
            wrapper.style.transform = `translate3d(${translateX}px, 0px, 0px)`
          }
          window.addEventListener('resize', debounce(setTransform, 500), { passive: true })
          pswp.on('change', () => {
            if (refresh) return
            if (prevIndex >= 0) {
              bullets[prevIndex].classList.remove('pswp__bullet--active')
            }
            bullets[pswp.currIndex].classList.add('pswp__bullet--active')
            setTransform()
            prevIndex = pswp.currIndex
          })
        }
      })

      // 分页显示器
      lightbox.value.pswp.ui.registerElement({
        name: 'page',
        order: 5,
        onInit: (el, pswp) => {
          pswp.on('change', () => {
            el.innerText = `${pswp.currIndex + 1}/${pswp.options.dataSource.length}`
          })
        }
      })
    })
    lightbox.value.init()
  }
  onMounted(() => {
    if (!lightbox.value) {
      if (isInit) initPS()
    }
  })

  onBeforeUnmount(() => {
    lightbox.value?.destroy?.()
    lightbox.value = null
  })

  return { lightbox, initPS }
}

export default useSimpleGallery
